import { useEffect, useRef } from "react";
import { toast } from "sonner";

/**
 * Service Worker update prompt.
 *
 * When a production deploy ships a new frontend bundle, browsers with an
 * older service worker version download the new SW in the background and
 * park it in the "waiting" state. Historically, users would only see the
 * new bundle on their NEXT browser session (whenever they closed the tab
 * and reopened it) — which meant every deploy silently split the user
 * base into "on new code" vs "still on old code" for hours or days. That
 * cache-drift class of bug is what caused the "Invalid Token / bouncing"
 * saga on 2026-02-19.
 *
 * This hook closes the drift window explicitly:
 *   1. On mount, subscribes to the active `ServiceWorkerRegistration`'s
 *      `updatefound` event.
 *   2. When a new worker transitions to `installed` AND there is already
 *      an active controller (meaning: it's an UPDATE, not the very first
 *      install), fires a persistent toast asking the user to reload.
 *   3. On click, posts `{type: 'SKIP_WAITING'}` to the waiting worker so
 *      `sw.js`'s existing message handler activates it immediately, then
 *      hard-reloads the page. The next page load runs the freshest JS.
 *
 * The component itself renders nothing — it's pure lifecycle.
 *
 * Guardrails:
 *   * Only fires when `navigator.serviceWorker.controller` already exists
 *     — no toast on the very first install.
 *   * Only shows one toast at a time (sonner handles dedup by id).
 *   * Also polls `registration.update()` every 5 minutes AND on tab
 *     visibility restore, so long-running tabs eventually notice new
 *     builds without the user having to reload manually first.
 */
export const useServiceWorkerUpdate = () => {
  const registrationRef = useRef(null);
  const promptedRef = useRef(false);

  useEffect(() => {
    if (typeof window === "undefined" || !("serviceWorker" in navigator)) {
      return undefined;
    }

    const showUpdatePrompt = (waitingWorker) => {
      if (promptedRef.current || !waitingWorker) return;
      promptedRef.current = true;

      toast(
        "A new version of HospiceGuard is available",
        {
          id: "sw-update-available",
          description:
            "Reload now to get the latest fixes and features. Your work is safe.",
          duration: Infinity,  // persistent until dismissed / clicked
          action: {
            label: "Reload",
            onClick: () => {
              // Ask the waiting SW to activate. `sw.js` listens for
              // {type: 'SKIP_WAITING'} and calls self.skipWaiting().
              try {
                waitingWorker.postMessage({ type: "SKIP_WAITING" });
              } catch {
                /* fall through to hard reload */
              }
              // Hard reload once the new SW takes control.
              const doReload = () => {
                try {
                  window.location.reload();
                } catch {
                  /* no-op */
                }
              };
              // If the new SW activates fast, `controllerchange` fires;
              // otherwise our 1.5s fallback catches slow activations.
              navigator.serviceWorker.addEventListener(
                "controllerchange",
                doReload,
                { once: true },
              );
              setTimeout(doReload, 1500);
            },
          },
          cancel: {
            label: "Later",
            onClick: () => {
              promptedRef.current = false;  // allow re-prompt on next update
            },
          },
        },
      );
    };

    const attachUpdateListener = (registration) => {
      registrationRef.current = registration;

      // If a worker is ALREADY waiting when we mount (page opened AFTER
      // the update installed), prompt immediately.
      if (registration.waiting && navigator.serviceWorker.controller) {
        showUpdatePrompt(registration.waiting);
      }

      // Listen for future updates.
      registration.addEventListener("updatefound", () => {
        const newWorker = registration.installing;
        if (!newWorker) return;
        newWorker.addEventListener("statechange", () => {
          if (
            newWorker.state === "installed" &&
            navigator.serviceWorker.controller
          ) {
            showUpdatePrompt(newWorker);
          }
        });
      });
    };

    navigator.serviceWorker
      .getRegistration()
      .then((reg) => {
        if (reg) attachUpdateListener(reg);
      })
      .catch(() => {
        /* SW may not be registered yet; the load event registration in
           index.html happens after this hook mounts in most cases, so we
           also subscribe to `controllerchange` as a fallback. */
      });

    // Fallback: when a fresh SW takes control (first install OR after our
    // skipWaiting), retrieve the registration and attach the listener so
    // subsequent updates still prompt.
    const controllerChange = () => {
      navigator.serviceWorker
        .getRegistration()
        .then((reg) => {
          if (reg && !registrationRef.current) attachUpdateListener(reg);
        })
        .catch(() => { /* no-op */ });
    };
    navigator.serviceWorker.addEventListener("controllerchange", controllerChange);

    // Poll for updates every 5 minutes so long-running tabs don't miss
    // a deploy.
    const pollId = window.setInterval(() => {
      if (registrationRef.current) {
        registrationRef.current.update().catch(() => { /* no-op */ });
      }
    }, 5 * 60 * 1000);

    // Also poll when tab regains visibility — the common "user comes back
    // from lunch" case.
    const onVisible = () => {
      if (document.visibilityState === "visible" && registrationRef.current) {
        registrationRef.current.update().catch(() => { /* no-op */ });
      }
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      navigator.serviceWorker.removeEventListener("controllerchange", controllerChange);
      document.removeEventListener("visibilitychange", onVisible);
      window.clearInterval(pollId);
    };
  }, []);
};

/**
 * Convenience mount-point: put `<ServiceWorkerUpdatePrompt />` anywhere
 * inside your app tree once and forget about it.
 */
export const ServiceWorkerUpdatePrompt = () => {
  useServiceWorkerUpdate();
  return null;
};

export default ServiceWorkerUpdatePrompt;
